import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { GridFieldType, PassportFieldPaletteSource } from './types'

const FIELD_TYPE_TRANSLATION_KEYS: Record<string, string> = {
  string: 'itf.field_type_string',
  text: 'itf.field_type_string',
  number: 'itf.field_type_number',
  integer: 'itf.field_type_number',
  float: 'itf.field_type_number',
  percent: 'itf.field_type_percent',
  date: 'itf.field_type_date',
  datetime: 'itf.field_type_date',
  bool: 'itf.field_type_boolean',
  boolean: 'itf.field_type_boolean',
  range: 'itf.field_type_range',
  iterator: 'itf.field_type_iterator',
  interator_simple: 'itf.field_type_iterator',
  computed: 'itf.field_type_computed',
  address: 'itf.field_type_address',
  year: 'itf.field_type_year',
  phone: 'itf.field_type_phone',
  email: 'itf.field_type_email',
}

/**
 * Возвращает ключ перевода для известного кода типа поля.
 */
export const fieldTypeTranslationKey = (fieldType?: string | null): string | null => {
  if (!fieldType) return null
  return FIELD_TYPE_TRANSLATION_KEYS[fieldType.toLowerCase()] ?? null
}

/**
 * Element-link selector cell dragged from «Связи → Элементы»: the cell itself selects the linked object.
 * Read-only element-linked field cells have a different `inventory_field_id` (the target field).
 */
export function isElementLinkSelectorCell(item: GridFieldType): boolean {
  const inv = item.inventory_field_id
  return (
    String(item.id).startsWith('element-link:') ||
    (item.palette_source === 'element' && item.link_field_id != null && inv === item.link_field_id)
  )
}

/**
 * Element-linked field cell dragged from «Связи → Элементы»: read-only value resolved through the selected linked object.
 * Excludes the editable selector cell (`element-link:*` or `palette_source === 'element'` with `inventory_field_id === link_field_id`).
 */
export function isElementLinkedFieldCell(item: GridFieldType): boolean {
  if (isElementLinkSelectorCell(item)) return false
  if (String(item.id).startsWith('element:')) return true
  return item.link_field_id != null && item.inventory_field_id != null
}

/**
 * Linked «Справочники» / composite grid cell — used for read-only chrome and linked-passport resolution hints.
 * When `palette_source === 'parent'`, only `id.startsWith('ref:')` counts (not `nested_binding`; backend may omit it).
 * Element-link selector cells are editable and therefore not treated as linked reference cells.
 */
export function isLinkedReferenceGridLayoutItem(item: GridFieldType): boolean {
  if (isElementLinkSelectorCell(item)) return false
  const id = String(item.id)
  if (id.startsWith('parent-acc-')) return true
  if (item.palette_source === 'element') {
    return id.startsWith('element:')
  }
  if (item.palette_source === 'parent') {
    return id.startsWith('ref:')
  }
  return id.startsWith('ref:') || Boolean(item.nested_binding?.segments?.length)
}

/**
 * Sidebar palette bucket for a grid cell — same rules as
 * `PassportBuilderGridRow.resolvePaletteSource` (own / parent «Справочники» / list «Перечень»).
 */
export function resolvePassportFieldPaletteSource(
  item: GridFieldType,
  catalogTypeId: number,
  fieldMeta?: ObjectInventoryField | null,
): PassportFieldPaletteSource {
  const cat = catalogTypeId
  const isOwnInventoryField = fieldMeta != null && cat > 0 && Number(fieldMeta.object_type) === cat
  const invFromCell = item.inventory_type != null ? Number(item.inventory_type) : NaN
  const nested = item.nested_binding?.segments
  const hasNestedPath = Boolean(nested && nested.length > 0)
  /** Mislabeled parent cells: composite `ref:` path is authoritative for `palette_source === 'parent'`. */
  const parentRefComposite = String(item.id).startsWith('ref:')

  if (item.palette_source && cat > 0) {
    /**
     * Only remap persisted `parent` → `own` when the field is actually on the catalog type.
     * `cellSameCatalog` alone is wrong for «Справочники» cells that still carry the catalog
     * `inventory_type` but point at parent-type field metadata (passport runtime would show inputs).
     */
    if (item.palette_source === 'parent' && !parentRefComposite && isOwnInventoryField) {
      return 'own'
    }
    if (item.palette_source === 'element') {
      return 'element'
    }
    return item.palette_source
  }
  if (hasNestedPath) return 'parent'
  if (item.type !== 'table' && item.list_group_id != null) return 'list'
  if (isOwnInventoryField) return 'own'
  if (Number.isFinite(invFromCell) && invFromCell > 0 && cat > 0 && invFromCell !== cat)
    return 'parent'
  return 'own'
}

/**
 * Возвращает локализованную подпись типа поля для бейджа конструктора.
 */
export function fieldTypeBadgeLabel(
  fieldMeta: ObjectInventoryField | null | undefined,
  translate: (key: string) => string,
): string {
  const t = fieldMeta?.type
  const translationKey = fieldTypeTranslationKey(t ?? 'string')
  return translationKey ? translate(translationKey) : String(t)
}
